import { useState } from "react";
import {
  ArrowLeft,
  Smile,
  Frown,
  Meh,
  Angry,
  Heart,
  TrendingUp,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Textarea } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/textarea";
import { Slider } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/slider";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { toast } from "sonner";

const EmotionsStress = () => {
  const navigate = useNavigate();
  const [selectedMood, setSelectedMood] = useState<string>("");
  const [stressLevel, setStressLevel] = useState([5]);
  const [notes, setNotes] = useState("");

  const moods = [
    { name: "Happy", icon: Smile, color: "text-chart-2", bg: "bg-chart-2/10" },
    { name: "Neutral", icon: Meh, color: "text-chart-1", bg: "bg-chart-1/10" },
    { name: "Sad", icon: Frown, color: "text-info", bg: "bg-info/10" },
    {
      name: "Stressed",
      icon: Angry,
      color: "text-destructive",
      bg: "bg-destructive/10",
    },
    { name: "Calm", icon: Heart, color: "text-primary", bg: "bg-primary/10" },
  ];

  const weeklyData = [
    { day: "Mon", stress: 6, mood: 7 },
    { day: "Tue", stress: 7, mood: 6 },
    { day: "Wed", stress: 5, mood: 8 },
    { day: "Thu", stress: 8, mood: 5 },
    { day: "Fri", stress: 4, mood: 9 },
    { day: "Sat", stress: 3, mood: 9 },
    { day: "Sun", stress: 5, mood: 8 },
  ];

  const handleSubmit = () => {
    if (!selectedMood) {
      toast.error("Please select a mood");
      return;
    }
    toast.success("Emotions and stress logged successfully!");
    setTimeout(() => navigate("/home"), 1000);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 border-b border-border bg-background">
        <div className="flex items-center gap-3 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="h-9 w-9"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Emotions & Stress
          </h1>
        </div>
      </div>

      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Mood Selection */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-foreground">
            How are you feeling?
          </h2>
          <div className="grid grid-cols-5 gap-3">
            {moods.map(mood => (
              <button
                key={mood.name}
                onClick={() => setSelectedMood(mood.name)}
                className={`flex flex-col items-center gap-2 rounded-xl p-3 transition-all hover:scale-105 ${
                  selectedMood === mood.name
                    ? `${mood.bg} border-2 border-current ${mood.color}`
                    : "bg-muted/20 hover:bg-muted/30"
                }`}
              >
                <mood.icon
                  className={`h-8 w-8 ${selectedMood === mood.name ? mood.color : "text-muted-foreground"}`}
                />
                <span className="text-xs font-medium">{mood.name}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Stress Level */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-2 text-base font-semibold text-foreground">
            Stress Level
          </h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Current level:{" "}
            <span className="font-semibold text-foreground">
              {stressLevel[0]}/10
            </span>
          </p>
          <div className="space-y-2">
            <Slider
              value={stressLevel}
              onValueChange={setStressLevel}
              max={10}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>No Stress</span>
              <span>Very Stressed</span>
            </div>
          </div>
        </Card>

        {/* Notes */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-3 text-base font-semibold text-foreground">
            Add Notes
          </h2>
          <Textarea
            placeholder="What's on your mind? Any triggers or positive moments?"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="min-h-[100px] resize-none"
          />
        </Card>

        {/* Weekly Trend */}
        <Card className="rounded-xl border border-border/50 bg-card p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-foreground">
            Weekly Trend
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="day"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  domain={[0, 10]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="stress"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--destructive))", r: 4 }}
                  name="Stress Level"
                />
                <Line
                  type="monotone"
                  dataKey="mood"
                  stroke="hsl(var(--chart-2))"
                  strokeWidth={2.5}
                  dot={{ fill: "hsl(var(--chart-2))", r: 4 }}
                  name="Mood Score"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex justify-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 rounded-full bg-destructive" />
              <span className="text-muted-foreground">Stress Level</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="bg-chart-2 h-3 w-3 rounded-full" />
              <span className="text-muted-foreground">Mood Score</span>
            </div>
          </div>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          className="h-12 w-full rounded-lg text-sm font-semibold"
        >
          Log Emotions & Stress
        </Button>
      </div>
    </div>
  );
};

export default EmotionsStress;
